// Sentry.init 에 넘길 설정을 import.meta.env 값만으로 만든다. SDK 를 import 하지 않아 jsdom 에서 가볍게 검증된다.
// "무엇을 보낼지" 는 파이프라인이 정하므로(D-11) 여기엔 켜고 끄기·환경·통합 선택만 있다.

import { type ReportingEnvironment, resolveEnvironment } from './environment'
import { stripAuthParams } from './toReportEvent'

export type SentryOptionsInput = {
  dsn?: string
  vercelEnv?: string
  releaseId: string
  sentryEnabledFlag?: string
}

export type NamedIntegration = { name: string }
export type Breadcrumb = { category?: string; data?: Record<string, unknown> }

export type SentryOptions = {
  dsn?: string
  enabled: boolean
  environment: ReportingEnvironment
  release?: string
  debug: boolean
  sendDefaultPii: false
  tracesSampleRate: 0
  integrations: (defaults: NamedIntegration[]) => NamedIntegration[]
  beforeBreadcrumb: (crumb: Breadcrumb) => Breadcrumb | null
}

// GlobalHandlers: 전역 에러는 installGlobalHandlers 가 잡는다(S2-1). EventFilters(옛 InboundFilters): 서버 필터와
// 같은 취지로 끈다(D-14). Console: console 출력을 수집하지 않는다(P-03). BrowserApiErrors: setTimeout·addEventListener
// 콜백을 감싸 예외를 직접 captureException 하므로(파이프라인 우회) 끈다 — 로컬 검증에서 실측.
export const DISABLED_INTEGRATIONS: readonly string[] = [
  'GlobalHandlers',
  'EventFilters',
  'Console',
  'BrowserApiErrors',
]

const UNKNOWN_RELEASE = 'unknown'

// fetch·xhr 는 data.url, history(navigation) 는 data.from·data.to 에 쿼리까지 실린다.
// CRM 로그인은 토큰을 쿼리로 받았다가 replaceState 로 지우는데, 그 순간의 navigation breadcrumb 이 세션 내내 남는다.
const URL_FIELDS = ['url', 'from', 'to'] as const

const beforeBreadcrumb = (crumb: Breadcrumb): Breadcrumb | null => {
  if (crumb.category === 'console') return null
  if (!crumb.data) return crumb
  const data = { ...crumb.data }
  for (const field of URL_FIELDS) {
    const value = data[field]
    if (typeof value === 'string') data[field] = stripAuthParams(value)
  }
  return { ...crumb, data }
}

export const buildSentryOptions = (input: SentryOptionsInput): SentryOptions => {
  const environment = resolveEnvironment(input.vercelEnv)
  const hasDsn = Boolean(input.dsn)
  const localOptIn = input.sentryEnabledFlag === 'true'

  return {
    dsn: input.dsn || undefined,
    enabled: hasDsn && (environment !== 'local' || localOptIn),
    environment,
    release: input.releaseId === UNKNOWN_RELEASE ? undefined : input.releaseId,
    debug: environment === 'local',
    sendDefaultPii: false,
    tracesSampleRate: 0,
    integrations: (defaults) => defaults.filter((i) => !DISABLED_INTEGRATIONS.includes(i.name)),
    beforeBreadcrumb,
  }
}
